import React, { useEffect, useRef, useState } from 'react';
import * as d3 from 'd3';
import './Chart.css';

const ClusteringPlot = ({ clusteringData, selectedCountyFips, onCountySelect, selectedFeature = 'prevalence' }) => {
  const svgRef = useRef();
  const containerRef = useRef();
  const [dimensions, setDimensions] = useState({ width: 0, height: 0 });

  useEffect(() => {
    if (!containerRef.current) return;

    const updateDimensions = () => {
      if (containerRef.current) {
        setDimensions({
          width: containerRef.current.clientWidth,
          height: containerRef.current.clientHeight
        });
      }
    };

    updateDimensions();
    const resizeObserver = new ResizeObserver(updateDimensions);
    resizeObserver.observe(containerRef.current);

    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (!clusteringData || clusteringData.length === 0 || dimensions.width === 0) return;

    const svg = d3.select(svgRef.current);
    
    // Setup layers if they don't exist
    let g = svg.select('.main-group');
    if (g.empty()) {
      g = svg.append('g').attr('class', 'main-group');
      g.append('g').attr('class', 'contours-group');
      g.append('g').attr('class', 'axis-x');
      g.append('g').attr('class', 'axis-y');
      g.append('g').attr('class', 'constellation-lines');
      g.append('g').attr('class', 'dots-group');
      g.append('g').attr('class', 'voronoi-group');
    }

    const margin = { top: 20, right: 20, bottom: 40, left: 40 };
    const width = dimensions.width - margin.left - margin.right;
    const height = dimensions.height - margin.top - margin.bottom;

    svg.attr('width', width + margin.left + margin.right)
       .attr('height', height + margin.top + margin.bottom);

    g.attr('transform', `translate(${margin.left},${margin.top})`);

    const plotData = clusteringData.map(d => ({
      fips: d.fips,
      name: d.name,
      x: d[selectedFeature]?.x ?? 0,
      y: d[selectedFeature]?.y ?? 0
    }));

    // Scales
    const xExtent = d3.extent(plotData, d => d.x);
    const yExtent = d3.extent(plotData, d => d.y);
    const xPadding = (xExtent[1] - xExtent[0]) * 0.1;
    const yPadding = (yExtent[1] - yExtent[0]) * 0.1;

    const x = d3.scaleLinear()
      .domain([xExtent[0] - xPadding, xExtent[1] + xPadding])
      .range([0, width]);

    const y = d3.scaleLinear()
      .domain([yExtent[0] - yPadding, yExtent[1] + yPadding])
      .range([height, 0]);

    // Axes
    const xAxis = d3.axisBottom(x).ticks(5);
    const yAxis = d3.axisLeft(y).ticks(5);

    g.select('.axis-x')
      .attr('transform', `translate(0,${height})`)
      .call(xAxis);

    g.select('.axis-y')
      .call(yAxis);

    // Density Contours
    const densityData = d3.contourDensity()
      .x(d => x(d.x))
      .y(d => y(d.y))
      .size([width, height])
      .bandwidth(20)
      (plotData);

    const contourColor = d3.scaleSequential(d3.interpolateBlues)
      .domain([0, d3.max(densityData, d => d.value)]);

    g.select('.contours-group')
      .selectAll('path')
      .data(densityData)
      .join('path')
      .attr('d', d3.geoPath())
      .attr('fill', d => contourColor(d.value))
      .attr('opacity', 0.15)
      .attr('stroke', 'none');

    // Helper to draw links and update Voronoi
    const updateVisuals = (currentX, currentY) => {
      const delaunay = d3.Delaunay.from(plotData, d => currentX(d.x), d => currentY(d.y));
      const voronoi = delaunay.voronoi([0, 0, width, height]);

      // Voronoi Interaction Layer
      g.select('.voronoi-group')
        .selectAll('path')
        .data(plotData)
        .join('path')
        .attr('d', (d, i) => voronoi.renderCell(i))
        .attr('fill', 'transparent')
        .style('cursor', 'pointer')
        .on('click', (e, d) => onCountySelect(d.fips))
        .on('mouseover', function(event, d) {
          // Check parent group's flag for animation state
          const voronoiGroup = this.parentNode;
          if (voronoiGroup.isAnimating) return;
          
          // Reduce opacity of other dots
          g.select('.dots-group').selectAll('circle')
            .filter(circleD => circleD.fips !== d.fips)
            .transition().duration(200)
            .style('opacity', 0.2);

          // Reduce opacity of contours
          g.select('.contours-group')
            .transition().duration(200)
            .style('opacity', 0.05);

          // Highlight dot
          g.select('.dots-group').selectAll('circle')
            .filter(circleD => circleD.fips === d.fips)
            .transition().duration(200)
            .attr('r', 10)
            .style('opacity', 1);

          // Draw Constellation Lines with contrast
          const index = plotData.indexOf(d);
          const neighbors = Array.from(delaunay.neighbors(index));
          const linesData = neighbors.map(i => ({
             source: d,
             target: plotData[i]
          }));

          g.select('.constellation-lines')
            .selectAll('line')
            .data(linesData)
            .join('line')
            .attr('x1', l => currentX(l.source.x))
            .attr('y1', l => currentY(l.source.y))
            .attr('x2', l => currentX(l.target.x))
            .attr('y2', l => currentY(l.target.y))
            .attr('stroke', '#2C3E50') // Darker color for contrast
            .attr('stroke-width', 1.5)
            .attr('opacity', 0)
            .transition().duration(200)
            .attr('opacity', 0.6);
        })
        .on('mouseout', function(event, d) {
           // Reset other dots opacity
           g.select('.dots-group').selectAll('circle')
            .interrupt() // Stop running transitions
            .style('opacity', 0.8);
           
           // Reset contours opacity
           g.select('.contours-group')
            .interrupt() // Stop running transitions
            .style('opacity', 1);

           // Reset highlighted dot
           g.select('.dots-group').selectAll('circle')
            .filter(circleD => circleD.fips === d.fips)
            .interrupt() // Stop running transitions
            .attr('r', d.fips === selectedCountyFips ? 8 : 5)
            .style('opacity', 0.8);
           
           // Remove lines
           g.select('.constellation-lines').selectAll('line').remove();
        })
        .append('title')
        .text(d => `${d.name}\nFIPS: ${d.fips}`);
    };

    // Dots with transition and VDS colors
    const dots = g.select('.dots-group').selectAll('.cluster-dot')
      .data(plotData, d => d.fips);

    // Flag to track animation state attached to the node to persist correctly
    // We attach it to the voronoi group node as a convenient centralized place
    const voronoiNode = g.select('.voronoi-group').node();
    if (voronoiNode) voronoiNode.isAnimating = true;

    dots.join(
      enter => enter.append('circle')
        .attr('class', 'cluster-dot')
        .attr('cx', d => x(d.x))
        .attr('cy', d => y(d.y))
        .attr('r', 0)
        .style('fill', '#2C3E50') // Dark Slate Blue for better contrast
        .style('stroke', 'white')
        .style('stroke-width', 1)
        .style('opacity', 0.8)
        .call(enter => enter.transition().duration(750)
            .attr('r', d => d.fips === selectedCountyFips ? 8 : 5)
            .on('end', () => { 
                if (voronoiNode) voronoiNode.isAnimating = false; 
            })),
      update => update
        .style('fill', d => d.fips === selectedCountyFips ? '#E74C3C' : '#2C3E50') // Red for selection
        .style('stroke', d => d.fips === selectedCountyFips ? 'black' : 'white')
        .style('stroke-width', d => d.fips === selectedCountyFips ? 2 : 1)
        .call(update => update.transition().duration(750)
          .attr('cx', d => x(d.x))
          .attr('cy', d => y(d.y))
          .attr('r', d => d.fips === selectedCountyFips ? 8 : 5)
          .on('end', () => { 
              if (voronoiNode) voronoiNode.isAnimating = false; 
          }))
    );

    // Initial Voronoi render
    updateVisuals(x, y);
    
    // Pass the voronoi node to updateVisuals so it can check the flag
    g.select('.voronoi-group').each(function() {
        this.isAnimating = true;
        // Ensure flag is reset after the initial transition duration if not caught by 'end'
        setTimeout(() => { this.isAnimating = false; }, 750);
    });

  }, [clusteringData, selectedCountyFips, onCountySelect, selectedFeature, dimensions]);

  return (
    <div ref={containerRef} className="chart-container">
      {(!clusteringData || clusteringData.length === 0) && (
        <div className="placeholder">Loading clustering data...</div>
      )}
      <svg ref={svgRef} style={{ width: '100%', height: '100%' }} />
    </div>
  );
};

export default ClusteringPlot;
